import { useState, useEffect } from 'react'
import DashboardLayout from '../Layout/DashboardLayout'
import axios from 'axios'
import { BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import { useAuth } from '../AuthContext'
import Loading from '../element/Loading'
import { PlusCircle, SquarePen, Trash2, X, Shield } from 'lucide-react'

export default function UsersPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const token = Cookies.get('WEB_SSR')

  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role_id: '',
  })

  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/users`, { headers }),
        axios.get(`${BASE_URL}/api/roles`, { headers }),
      ])
      if (usersRes.data.success) setUsers(usersRes.data.data || [])
      if (rolesRes.data.success) setRoles(rolesRes.data.data || [])
    } catch (error) {
      console.error(error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  const openAddModal = () => {
    setEditingUser(null)
    setForm({ name: '', email: '', password: '', password_confirmation: '', role_id: '' })
    setModalOpen(true)
  }

  const openEditModal = (user) => {
    setEditingUser(user)
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      password_confirmation: '',
      role_id: user.role_id || '',
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.name || !form.email) {
      toast.error('Please fill in required fields')
      return
    }
    if (!editingUser && !form.password) {
      toast.error('Password is required')
      return
    }
    if (form.password && form.password !== form.password_confirmation) {
      toast.error('Passwords do not match')
      return
    }

    setSaving(true)
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role_id: form.role_id || null,
      }
      if (form.password) payload.password = form.password

      if (editingUser) {
        const response = await axios.put(`${BASE_URL}/api/users/${editingUser.id}`, payload, { headers })
        if (response.data.success) {
          toast.success(t('users.update_success'))
          fetchData()
          setModalOpen(false)
        }
      } else {
        const response = await axios.post(`${BASE_URL}/api/users`, payload, { headers })
        if (response.data.success) {
          toast.success(t('users.add_success'))
          fetchData()
          setModalOpen(false)
        }
      }
    } catch (error) {
      console.error(error)
      toast.error(t('users.error.saving'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    setDeleting(true)
    try {
      const response = await axios.delete(`${BASE_URL}/api/users/${id}`, { headers })
      if (response.data.success) {
        toast.success(t('users.delete_success'))
        fetchData()
        setDeleteConfirm(null)
      }
    } catch (error) {
      console.error(error)
      toast.error(t('users.error.deleting'))
    } finally {
      setDeleting(false)
    }
  }

  // if (!can('users.manage')) {
  //   return (
  //     <DashboardLayout>
  //       <div className="flex items-center justify-center min-h-[50vh]">
  //         <p className="text-gray-500">Unauthorized</p>
  //       </div>
  //     </DashboardLayout>
  //   )
  // }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl max-lg:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">{t('users.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('users.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex items-center gap-2 rounded-lg bg-[#171717] px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
          >
            <PlusCircle className="w-4 h-4" />
            {t('users.add')}
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2">
              <Loading size={10} />  
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">{t('users.no_users')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-gray-900 dark:text-gray-100">
                <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3">{t('users.name')}</th>
                    <th className="px-4 py-3">{t('users.email')}</th>
                    <th className="px-4 py-3">{t('users.role')}</th>
                    <th className="px-4 py-3 text-right">{t('users.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-4 py-3 align-top font-medium">{u.name}</td>
                      <td className="px-4 py-3 align-top text-gray-500">{u.email}</td>
                      <td className="px-4 py-3 align-top">
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          <Shield className="w-3 h-3" />
                          {u.role ? u.role.name : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(u)}
                            className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            <SquarePen className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(u)}
                            className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {editingUser ? t('users.edit') : t('users.add')}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                <span>{t('users.name')}</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  placeholder={t('users.name_placeholder')}
                />
              </label>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                <span>{t('users.email')}</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  placeholder={t('users.email_placeholder')}
                />
              </label>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                <span>{editingUser ? t('users.password_optional') : t('users.password')}</span>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  placeholder={t('users.password_placeholder')}
                />
              </label>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                <span>{t('users.password_confirmation')}</span>
                <input
                  type="password"
                  value={form.password_confirmation}
                  onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  placeholder={t('users.password_placeholder')}
                />
              </label>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                <span>{t('users.role')}</span>
                <select
                  value={form.role_id}
                  onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">{t('users.select_role')}</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-lg bg-[#171717] px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {saving ? t('users.saving') : (editingUser ? t('users.edit') : t('users.add'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirm(null)}>
          <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('users.delete_confirm')}</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{deleteConfirm.name} ({deleteConfirm.email})</p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('users.cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={deleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-70"
              >
                {deleting ? t('users.deleting') : t('users.delete_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
